// Styles
import '../../MapStyles/MapMenu.scss';

// Local Components
import './MapInfoItem';

// External Libraries
import { useContext, useState } from 'react';
import { Accordion, AccordionContext, Button, Card, useAccordionButton } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

// App Services & Store
import { Api } from '@services';
import { useMapStore } from '../../MapStore/UseMapStore';

// App Components
import { BeaconsReadingsCarousel, Keywords, Loading, Text } from '@components';

async function fetchReadings({ feature }) {
  if (
    feature.properties.EntityType === 'Asset' ||
    feature.properties.EntityType === 'Stationary Asset'
  ) {
    const { data } = await Api.get(`/v2/assets/${feature.properties.Id}/readings`);
    return data;
  } else {
    return null;
  }
}

function ContextAwareToggle({ eventKey, icon, title }) {
  const { activeEventKey } = useContext(AccordionContext);

  const decoratedOnClick = useAccordionButton(eventKey);

  const isCurrentEventKey = activeEventKey === eventKey;

  return (
    <Button
      title={title}
      className={`d-flex flex-row align-items-center justify-content-between ${
        isCurrentEventKey ? '' : 'nav-text-icon'
      }`}
      variant={isCurrentEventKey ? 'primary' : 'light'}
      onClick={decoratedOnClick}
    >
      <i className={`sonarIcon sonarIcon-${icon} fs-md`} />
    </Button>
  );
}

export function MapInfoItem() {
  const showMore = useMapStore((state) => state.showMore);
  const setShowMore = useMapStore((state) => state.setShowMore);
  const [assetData, setAssetData] = useState(null);
  const { data, status } = useQuery(
    ['asset-readings', showMore],
    () => fetchReadings({ feature: showMore }),
    {
      enabled:
        showMore.properties.EntityType === 'Asset' ||
        showMore.properties.EntityType === 'Stationary Asset',
      onSuccess: (data) => setAssetData(data as any),
    },
  );
  const history = useHistory();

  return (
    <div className="object-wrapper">
      <Accordion
        defaultActiveKey={
          showMore.properties.EntityType === 'Asset' ||
          showMore.properties.EntityType === 'Stationary Asset'
            ? '1'
            : '0'
        }
      >
        <Card.Header className="d-flex flex-row align-items-center justify-content-between">
          <div className="d-flex flex-row align-items-center justify-content-between">
            <Text className="title-text user-select-none fw-semibold fs-md">
              {showMore.properties.Name}
            </Text>
          </div>
          <div className="d-flex flex-row align-items-center justify-content-between">
            {showMore.properties.EntityType !== 'Place' && (
              <ContextAwareToggle eventKey="0" icon={'circle-caret-down'} title={'Info'} />
            )}
            {(showMore.properties.EntityType === 'Asset' ||
              showMore.properties.EntityType === 'Stationary Asset') && (
              <ContextAwareToggle eventKey="1" icon={'s-pulse'} title={'Readings'} />
            )}
            {(showMore.properties.EntityType === 'Asset' ||
              showMore.properties.EntityType === 'Stationary Asset') && (
              <Button
                title="Go to Asset"
                className="btn-light d-flex justify-content-between align-items-center nav-text-icon"
                variant="light"
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  history.push('/assets/' + showMore.properties.Id);
                  e.stopPropagation();
                }}
              >
                <i className={`sonarIcon sonarIcon-open-in fs-md align-sub`} />
              </Button>
            )}
            {showMore.properties.EntityType === 'Place' && (
              <Button
                title="Go to Place"
                className="btn-light d-flex justify-content-between align-items-center nav-text-icon"
                variant="light"
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  history.push('/places/' + showMore.properties.Id);
                  e.stopPropagation();
                }}
              >
                <i className={`sonarIcon sonarIcon-open-in fs-md`} />
              </Button>
            )}
            <Button
              title={'Back'}
              className="btn-light d-flex flex-row align-items-center justify-content-between nav-text-icon"
              variant="light"
              onClick={() => {
                setShowMore(null);
              }}
            >
              <i className="sonarIcon sonarIcon-left-arrow fs-md" />
            </Button>
          </div>
        </Card.Header>

        <>
          <Accordion.Collapse eventKey="0">
            <Card.Body>
              {showMore && (
                <>
                  {showMore.properties.Id && (
                    <div className="status-row d-flex align-items-center">
                      <Text className="user-select-none fw-semibold fs-md">
                        {showMore.properties.EntityType} ID:&nbsp;
                      </Text>
                      <Text className="fs-md">{showMore.properties.Id}</Text>
                    </div>
                  )}
                  {showMore.properties.DeviceId && (
                    <div className="status-row d-flex align-items-center">
                      <Text className="user-select-none fw-semibold fs-md">Device ID: &nbsp;</Text>
                      <Text className="fs-md">{showMore.properties.DeviceId}</Text>
                    </div>
                  )}
                  {showMore.properties.LastCheckIn && (
                    <div className="status-row d-flex align-items-center">
                      <Text className="user-select-none fw-semibold fs-md">
                        Last Check-In: &nbsp;
                      </Text>
                      <Text className="fs-md">
                        {moment.utc(showMore.properties.LastCheckIn).local().format('lll')}
                      </Text>
                    </div>
                  )}
                  {showMore.properties.OnlineStatus && (
                    <div className="status-row d-flex align-items-center">
                      <Text className="user-select-none fw-semibold fs-md">Status: &nbsp;</Text>
                      <Text className="fs-md">
                        {showMore.properties.OnlineStatus.replace(/([a-z])([A-Z])/g, '$1 $2')}
                      </Text>
                    </div>
                  )}
                  {showMore.properties.Floor && (
                    <div className="status-row d-flex align-items-center">
                      <Text className="user-select-none fw-semibold fs-md">Floor: &nbsp;</Text>
                      <Text className="fs-md">{showMore.properties.Floor}</Text>
                    </div>
                  )}
                  {showMore.properties.EntityType === 'Place' &&
                    showMore.properties.Keywords.length > 0 && (
                      <div className="d-flex flex-column flex-wrap align-items-start">
                        <Text className="fw-bold fs-md me-2 mb-2">Keywords:</Text>
                        <Keywords
                          initKeywords={showMore.properties.Keywords}
                          canDeleteKeywords={false}
                        />
                      </div>
                    )}
                </>
              )}
            </Card.Body>
          </Accordion.Collapse>
          <Accordion.Collapse eventKey="1">
            <Card.Body>
              {status === 'loading' && <Loading />}
              {status === 'success' && (
                <div>
                  {assetData?.map((beacon, index) => (
                    <div className="d-flex flex-column" key={index}>
                      <Text className="title-text user-select-none fw-semibold fs-md">
                        {beacon.name}
                      </Text>
                      {beacon.readings.length > 0 ? (
                        <BeaconsReadingsCarousel
                          key={index}
                          readings={beacon.readings}
                          visibleCount={4}
                        />
                      ) : (
                        <Text
                          key={index}
                          className="title-text user-select-none fw-light fs-md mb-3"
                        >
                          There are currently no readings for this Beacon
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Accordion.Collapse>
        </>
      </Accordion>
    </div>
  );
}
